import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Users, Phone, Mail, CreditCard, FileText, Utensils, Palette, Volume2 } from 'lucide-react';
import { format } from 'date-fns';
import { BanquetBooking } from '../types';

interface CreateBanquetBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  hallId?: string;
  selectedDate?: Date;
  onCreateBooking: (bookingData: any) => Promise<void>;
}

const CreateBanquetBookingModal: React.FC<CreateBanquetBookingModalProps> = ({
  isOpen,
  onClose,
  hallId,
  selectedDate,
  onCreateBooking,
}) => {
  const [formData, setFormData] = useState({
    hallId: hallId || '',
    guestName: '',
    phone: '',
    email: '',
    eventName: '',
    eventType: 'wedding' as BanquetBooking['eventType'],
    eventDate: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    startTime: '10:00',
    endTime: '18:00',
    guestCount: 50,
    totalAmount: 0,
    advanceAmount: 0,
    specialRequirements: '',
    cateringRequired: false,
    decorationRequired: false,
    audioVisualRequired: false,
    bookingSource: 'direct',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Sample halls data - in real app, this would come from props or API
  const sampleHalls = [
    { id: 'HALL-001', name: 'Grand Ballroom', capacity: 200, hourlyRate: 5000 },
    { id: 'HALL-002', name: 'Royal Hall', capacity: 150, hourlyRate: 4000 },
    { id: 'HALL-003', name: 'Garden Pavilion', capacity: 100, hourlyRate: 3000 },
    { id: 'HALL-004', name: 'Conference Room A', capacity: 50, hourlyRate: 2000 },
  ];

  const eventTypes: { value: BanquetBooking['eventType']; label: string; icon: string }[] = [
    { value: 'wedding', label: 'Wedding', icon: '💒' },
    { value: 'corporate', label: 'Corporate Event', icon: '🏢' },
    { value: 'birthday', label: 'Birthday Party', icon: '🎂' },
    { value: 'conference', label: 'Conference', icon: '📊' },
    { value: 'other', label: 'Other', icon: '🎉' },
  ];

  const bookingSources = [
    { value: 'direct', label: 'Direct Booking' },
    { value: 'website', label: 'Website' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'walk-in', label: 'Walk-in' },
    { value: 'referral', label: 'Referral' },
  ];

  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        hallId: hallId || '',
        eventDate: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      }));
    }
  }, [isOpen, hallId, selectedDate]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const calculateTotalAmount = () => {
    const selectedHall = sampleHalls.find(hall => hall.id === formData.hallId);
    if (!selectedHall) return 0;
    
    const startTime = new Date(`2000-01-01T${formData.startTime}`);
    const endTime = new Date(`2000-01-01T${formData.endTime}`);
    const duration = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60); // hours
    
    return Math.max(1, duration) * selectedHall.hourlyRate;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.hallId) newErrors.hallId = 'Please select a banquet hall';
    if (!formData.guestName.trim()) newErrors.guestName = 'Guest name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.eventName.trim()) newErrors.eventName = 'Event name is required';
    if (!formData.eventDate) newErrors.eventDate = 'Event date is required';
    if (!formData.startTime) newErrors.startTime = 'Start time is required';
    if (!formData.endTime) newErrors.endTime = 'End time is required';
    if (formData.guestCount < 1) newErrors.guestCount = 'Guest count must be at least 1';
    if (formData.advanceAmount < 0) newErrors.advanceAmount = 'Advance amount cannot be negative';
    if (formData.advanceAmount > formData.totalAmount) {
      newErrors.advanceAmount = 'Advance amount cannot exceed total amount';
    }

    // Validate time range
    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}`);
      const end = new Date(`2000-01-01T${formData.endTime}`);
      if (start >= end) {
        newErrors.endTime = 'End time must be after start time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const totalAmount = calculateTotalAmount();
      const bookingData = {
        ...formData,
        totalAmount,
        balanceAmount: totalAmount - formData.advanceAmount,
      };
      
      await onCreateBooking(bookingData);
      onClose();
    } catch (error) {
      console.error('Error creating booking:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedHall = sampleHalls.find(hall => hall.id === formData.hallId);
  const totalAmount = calculateTotalAmount();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Create Banquet Booking</h2>
              <p className="text-primary-100 mt-1">Book a banquet hall for your event</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-6">
            {/* Hall Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Banquet Hall *
              </label>
              <select
                value={formData.hallId}
                onChange={(e) => handleInputChange('hallId', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.hallId ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select a banquet hall</option>
                {sampleHalls.map((hall) => (
                  <option key={hall.id} value={hall.id}>
                    {hall.name} - Capacity: {hall.capacity} guests - ₹{hall.hourlyRate}/hour
                  </option>
                ))}
              </select>
              {errors.hallId && <p className="text-red-500 text-sm mt-1">{errors.hallId}</p>}
            </div>

            {/* Event Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Name *
                </label>
                <input
                  type="text"
                  value={formData.eventName}
                  onChange={(e) => handleInputChange('eventName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.eventName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter event name"
                />
                {errors.eventName && <p className="text-red-500 text-sm mt-1">{errors.eventName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Type *
                </label>
                <select
                  value={formData.eventType}
                  onChange={(e) => handleInputChange('eventType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {eventTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Guest Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Guest Name *
                </label>
                <input
                  type="text"
                  value={formData.guestName}
                  onChange={(e) => handleInputChange('guestName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.guestName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter guest name"
                />
                {errors.guestName && <p className="text-red-500 text-sm mt-1">{errors.guestName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter phone number"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter email address"
                />
              </div>
            </div>

            {/* Event Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Date *
                </label>
                <input
                  type="date"
                  value={formData.eventDate}
                  onChange={(e) => handleInputChange('eventDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.eventDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.eventDate && <p className="text-red-500 text-sm mt-1">{errors.eventDate}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time *
                </label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => handleInputChange('startTime', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.startTime ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.startTime && <p className="text-red-500 text-sm mt-1">{errors.startTime}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time *
                </label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => handleInputChange('endTime', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.endTime ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.endTime && <p className="text-red-500 text-sm mt-1">{errors.endTime}</p>}
              </div>
            </div>

            {/* Guest Count and Booking Source */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Guest Count *
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.guestCount}
                  onChange={(e) => handleInputChange('guestCount', parseInt(e.target.value) || 0)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.guestCount ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.guestCount && <p className="text-red-500 text-sm mt-1">{errors.guestCount}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Booking Source
                </label>
                <select
                  value={formData.bookingSource}
                  onChange={(e) => handleInputChange('bookingSource', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {bookingSources.map((source) => (
                    <option key={source.value} value={source.value}>
                      {source.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Financial Information */}
            {selectedHall && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Financial Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Amount
                    </label>
                    <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg">
                      <span className="text-lg font-semibold text-gray-800">
                        ₹{totalAmount.toLocaleString()}
                      </span>
                      <p className="text-sm text-gray-500">
                        {selectedHall.hourlyRate}/hour × {Math.max(1, (new Date(`2000-01-01T${formData.endTime}`).getTime() - new Date(`2000-01-01T${formData.startTime}`).getTime()) / (1000 * 60 * 60))} hours
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Advance Amount *
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={totalAmount}
                      value={formData.advanceAmount}
                      onChange={(e) => handleInputChange('advanceAmount', parseFloat(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.advanceAmount ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.advanceAmount && <p className="text-red-500 text-sm mt-1">{errors.advanceAmount}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Balance Due
                    </label>
                    <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg">
                      <span className="text-lg font-semibold text-orange-600">
                        ₹{(totalAmount - formData.advanceAmount).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Event Requirements */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Event Requirements</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.cateringRequired}
                    onChange={(e) => handleInputChange('cateringRequired', e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <Utensils className="w-5 h-5 text-orange-500" />
                  <span className="text-sm font-medium text-gray-700">Catering Required</span>
                </label>

                <label className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.decorationRequired}
                    onChange={(e) => handleInputChange('decorationRequired', e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <Palette className="w-5 h-5 text-pink-500" />
                  <span className="text-sm font-medium text-gray-700">Decoration Required</span>
                </label>

                <label className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.audioVisualRequired}
                    onChange={(e) => handleInputChange('audioVisualRequired', e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <Volume2 className="w-5 h-5 text-purple-500" />
                  <span className="text-sm font-medium text-gray-700">Audio/Visual Required</span>
                </label>
              </div>
            </div>

            {/* Special Requirements */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Special Requirements
              </label>
              <textarea
                value={formData.specialRequirements}
                onChange={(e) => handleInputChange('specialRequirements', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Any special requirements or notes for the event..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4" />
                  <span>Create Booking</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBanquetBookingModal;
